import { UrlTree, Router } from '@angular/router';
import { of, from, Observable } from 'rxjs';
import { catchError, delay, map, switchMap } from 'rxjs/operators';
import { AuthenticationService } from '../services/auth.service';
import { LocalStorageService } from '../services/local-storage.service';
import { AlertService } from '../services/alert.service';

export const redirectTo = (router: Router, path: string | string[]): Observable<UrlTree> =>
  of(router.createUrlTree(Array.isArray(path) ? path : [path]));

export function ensureInitWithDeviceDelay(authService: AuthenticationService): Observable<void> {
  return from(authService.ensureInitialized()).pipe(
    map(() => undefined),
    catchError(() => of(undefined))
  );
}

export function loginRedirectUrl(router: Router, attemptedUrl: string): UrlTree {
  const returnUrl = attemptedUrl !== '/' && !attemptedUrl.includes('/auth/')
    ? attemptedUrl
    : '/';
  return router.createUrlTree(['/auth/login'], { queryParams: { returnUrl } });
}

export function clearAllAndRedirect(
  storage: LocalStorageService,
  router: Router,
  to: string | string[],
  alert?: AlertService,
  alertTitle?: string,
  alertMessage?: string
): Observable<UrlTree> {
  // Do not clear persisted auth/session on guard redirects.
  // Guards can run during transient startup/storage timing windows, and clearing
  // data here can force unnecessary logout loops.
  if (alert && alertTitle && alertMessage) {
    alert.error(alertTitle, alertMessage);
  }
  return redirectTo(router, to);
}

export function handleGuardFatal(
  storage: LocalStorageService,
  router: Router,
  fallbackPath: string | string[]
): Observable<UrlTree> {
  return redirectTo(router, fallbackPath);
}

export function requireAuthenticatedOrLogin(
  authService: AuthenticationService,
  storage: LocalStorageService,
  router: Router,
  stateUrl: string,
  alert?: AlertService,
  showAlertForPaths: string[] = []
): Observable<boolean | UrlTree> {
  return ensureInitWithDeviceDelay(authService).pipe(
    switchMap(() => authService.isAuthenticated$()),
    switchMap((isAuthenticated) => {
      if (isAuthenticated) {
        return of(true);
      }

      const shouldAlert = showAlertForPaths.some(p => stateUrl.includes(p));
      const urlTree = loginRedirectUrl(router, stateUrl);

      if (shouldAlert && alert) {
        alert.error('Authentication Required', 'Please login to access this feature.');
      }

      return of(urlTree);
    }),
    catchError((error) => {
      const urlTree = loginRedirectUrl(router, stateUrl);
      return of(urlTree);
    })
  );
}

export function allowIf(
  condition: boolean,
  router: Router,
  redirectPath: string | string[],
  alert?: AlertService,
  alertTitle?: string,
  alertMessage?: string
): boolean | UrlTree {
  if (condition) return true;
  if (alert && alertTitle && alertMessage) {
    alert.error(alertTitle, alertMessage);
  }
  return router.createUrlTree(Array.isArray(redirectPath) ? redirectPath : [redirectPath]);
}
